import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';
import Chip from '@mui/material/Chip';
import { Translate, TextFormat } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntity } from './booking.reducer';

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
    {children}
  </Typography>
);

const FieldValue = ({ children, sx }: { children: React.ReactNode; sx?: object }) => (
  <Typography variant="body1" sx={{ mb: 2, ...sx }}>
    {children || '-'}
  </Typography>
);

const BooleanDisplay = ({ value }: { value?: boolean }) =>
  value ? (
    <Chip icon={<FontAwesomeIcon icon="check" />} label="Yes" color="success" size="small" />
  ) : (
    <Chip icon={<FontAwesomeIcon icon="times" />} label="No" color="error" size="small" />
  );

export const BookingDetail = () => {
  const dispatch = useAppDispatch();

  const { id } = useParams<'id'>();

  useEffect(() => {
    dispatch(getEntity(id));
  }, []);

  const bookingEntity = useAppSelector(state => state.booking.entity);
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, p: 2 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <h2 data-cy="bookingDetailsHeading">
          <Translate contentKey="jhipsterSampleApplicationApp.booking.detail.title">Booking</Translate>
        </h2>
        {bookingEntity.id && (
          <Box sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', px: 2, py: 0.5, borderRadius: 1, fontWeight: 'bold' }}>
            ID: {bookingEntity.id}
          </Box>
        )}
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.booking.bookDatetime" />
          </FieldLabel>
          <FieldValue>
            {bookingEntity.bookDatetime ? (
              <TextFormat value={bookingEntity.bookDatetime} type="date" format={APP_LOCAL_DATE_FORMAT} />
            ) : null}
          </FieldValue>

          <Grid container spacing={2}>
            <Grid size={{ xs: 4 }}>
              <FieldLabel>
                <Translate contentKey="jhipsterSampleApplicationApp.booking.noPersons" />
              </FieldLabel>
              <FieldValue>{bookingEntity.noPersons}</FieldValue>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <FieldLabel>
                <Translate contentKey="jhipsterSampleApplicationApp.booking.noKids" />
              </FieldLabel>
              <FieldValue>{bookingEntity.noKids}</FieldValue>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <FieldLabel>
                <Translate contentKey="jhipsterSampleApplicationApp.booking.noPets" />
              </FieldLabel>
              <FieldValue>{bookingEntity.noPets}</FieldValue>
            </Grid>
          </Grid>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.booking.total" />
          </FieldLabel>
          <FieldValue>{bookingEntity.total}</FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.booking.paymentType" />
          </FieldLabel>
          <FieldValue>{bookingEntity.paymentType}</FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.booking.valid" />
          </FieldLabel>
          <BooleanDisplay value={bookingEntity.valid} />

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.booking.cancelledAt" />
          </FieldLabel>
          <FieldValue>
            {bookingEntity.cancelledAt ? <TextFormat value={bookingEntity.cancelledAt} type="date" format={APP_LOCAL_DATE_FORMAT} /> : null}
          </FieldValue>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.booking.remoteData" />
          </FieldLabel>
          <FieldValue sx={{ whiteSpace: 'pre-wrap' }}>{bookingEntity.remoteData}</FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.booking.remoteId" />
          </FieldLabel>
          <FieldValue>{bookingEntity.remoteId}</FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.booking.createdDate" />
          </FieldLabel>
          <FieldValue>
            {bookingEntity.createdDate ? <TextFormat value={bookingEntity.createdDate} type="date" format={APP_LOCAL_DATE_FORMAT} /> : null}
          </FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.booking.schedule" />
          </FieldLabel>
          <FieldValue>{bookingEntity.schedule ? bookingEntity.schedule.code : ''}</FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.booking.passenger" />
          </FieldLabel>
          <FieldValue>{bookingEntity.passenger ? bookingEntity.passenger.name : ''}</FieldValue>
        </Grid>

        <Grid size={12}>
          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button
              component={Link}
              to="/booking"
              replace
              color="info"
              variant="outlined"
              startIcon={<FontAwesomeIcon icon="arrow-left" />}
            >
              <Translate contentKey="entity.action.back">Back</Translate>
            </Button>
            <Button
              component={Link}
              to={`/booking/${bookingEntity.id}/edit`}
              replace
              color="primary"
              variant="contained"
              startIcon={<FontAwesomeIcon icon="pencil-alt" />}
            >
              <Translate contentKey="entity.action.edit">Edit</Translate>
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BookingDetail;
